import React, { useState } from 'react';
import { ShieldCheck, FileSearch, AlertTriangle, ScrollText, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';

// ---- Types ----

type ComplianceCategory = 'hmda' | 'fair_lending' | 'bsa_aml' | 'audit';
type ComplianceStatus = 'compliant' | 'review_needed' | 'flagged';

interface ComplianceItem {
  id: string;
  category: ComplianceCategory;
  title: string;
  status: ComplianceStatus;
  lastChecked: string;
  detail: string;
}

// ---- Mock data ----

const MOCK_COMPLIANCE_ITEMS: ComplianceItem[] = [
  {
    id: 'c-001',
    category: 'hmda',
    title: 'HMDA LAR — Q1 2026 Submission',
    status: 'compliant',
    lastChecked: 'Mar 15, 2026',
    detail: 'Loan Application Register validated. 847 records submitted to CFPB within deadline.',
  },
  {
    id: 'c-002',
    category: 'fair_lending',
    title: 'Fair Lending Disparity Analysis',
    status: 'review_needed',
    lastChecked: 'Mar 12, 2026',
    detail: 'Approval rate disparity detected between demographic segments. Compliance officer review required.',
  },
  {
    id: 'c-003',
    category: 'bsa_aml',
    title: 'BSA/AML Alert — Threshold Exceeded',
    status: 'flagged',
    lastChecked: 'Mar 17, 2026',
    detail: '3 members exceeded $10,000 cash transaction threshold. SAR filing required within 30 days.',
  },
  {
    id: 'c-004',
    category: 'audit',
    title: 'Audit Log Export — Last 90 Days',
    status: 'compliant',
    lastChecked: 'Mar 1, 2026',
    detail: '12,445 audit entries exported to Azure Blob WORM storage. 7-year retention policy active.',
  },
];

// ---- Category icons ----

const CATEGORY_ICONS: Record<ComplianceCategory, React.ElementType> = {
  hmda: ScrollText,
  fair_lending: FileSearch,
  bsa_aml: AlertTriangle,
  audit: Lock,
};

const CATEGORY_COLORS: Record<ComplianceCategory, string> = {
  hmda: 'bg-brand-tint text-brand-dark',
  fair_lending: 'bg-running-soft text-running-ink',
  bsa_aml: 'bg-warn-soft text-warn',
  audit: 'bg-sunken text-ink-soft',
};

// ---- Status badge ----

interface StatusBadgeProps {
  status: ComplianceStatus;
}

function StatusBadge({ status }: StatusBadgeProps): React.ReactElement {
  const config: Record<ComplianceStatus, { label: string; className: string; icon: React.ElementType }> = {
    compliant: {
      label: 'Compliant',
      className: 'bg-pass-soft text-pass',
      icon: ShieldCheck,
    },
    review_needed: {
      label: 'Review Needed',
      className: 'bg-warn-soft text-warn',
      icon: FileSearch,
    },
    flagged: {
      label: 'Flagged',
      className: 'bg-fail-soft text-fail',
      icon: AlertTriangle,
    },
  };

  const { label, className, icon: Icon } = config[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </span>
  );
}

// ---- CompliancePage ----

/**
 * CompliancePage — HMDA, fair lending, BSA/AML flags, audit export.
 * Displays compliance items with CU2-branded status badges and Lucide icons.
 * Access restricted: compliance_officer and admin roles only.
 */
export function CompliancePage(): React.ReactElement {
  const [showEmpty, setShowEmpty] = useState(false);

  const isEmpty = showEmpty || MOCK_COMPLIANCE_ITEMS.length === 0;

  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <SectionHeader title="Compliance" />
          <button
            type="button"
            onClick={() => setShowEmpty(!showEmpty)}
            className="text-xs text-ink-muted hover:text-ink-soft ml-2"
          >
            [Toggle empty]
          </button>
        </div>
        <p className="text-sm text-ink-muted mt-1">
          HMDA reporting, fair lending analysis, BSA/AML alerts, audit log export
        </p>
        <p className="text-xs text-ink-muted mt-1">
          Access restricted: compliance_officer and admin roles only
        </p>
      </div>

      {/* Compliance cards or empty state */}
      {isEmpty ? (
        <EmptyState
          icon={ShieldCheck}
          headline="Compliance dashboard clear"
          body="HMDA, fair lending, and BSA/AML compliance items will be tracked here."
        />
      ) : (
        <div className="grid gap-4">
          {MOCK_COMPLIANCE_ITEMS.map((item) => {
            const CategoryIcon = CATEGORY_ICONS[item.category];
            const iconColorClass = CATEGORY_COLORS[item.category];

            return (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-sunken shadow-sm p-5 flex items-start gap-4"
              >
                {/* Category icon */}
                <div
                  className={cn(
                    'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                    iconColorClass
                  )}
                >
                  <CategoryIcon className="h-5 w-5" aria-hidden="true" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <p className="text-sm font-semibold text-ink">{item.title}</p>
                    <StatusBadge status={item.status} />
                  </div>
                  <p className="text-sm text-ink-soft leading-relaxed">{item.detail}</p>
                  <p className="text-xs text-ink-muted mt-2">Last checked: {item.lastChecked}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
